import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useTranslation } from 'react-i18next';
import Button from '../Button';
import Input from '../Input';
import Textarea from '../Textarea';
import Select from '../Select';
import type { TaxonomyNode } from '../../pages/settings/TaxonomySettingsPage';

// Validation schema
const taxonomyNodeSchema = z.object({
  name: z.string().min(1, 'Name is required').max(100, 'Name must be less than 100 characters'),
  description: z.string().max(500, 'Description must be less than 500 characters').optional().or(z.literal('')),
  parentId: z.string().nullable().optional(),
  color: z
    .string()
    .regex(/^#[0-9A-Fa-f]{6}$/, 'Color must be a valid hex color (e.g., #FF5733)')
    .optional()
    .or(z.literal('')),
});

type TaxonomyNodeFormData = z.infer<typeof taxonomyNodeSchema>;

interface TaxonomyNodeFormProps {
  node?: TaxonomyNode | null; // If provided, edit mode; otherwise create mode
  parentNode?: TaxonomyNode | null; // Pre-select parent for "Add child" action
  allNodes: TaxonomyNode[]; // All nodes for parent selection
  onSubmit: (data: TaxonomyNodeFormData) => Promise<void>;
  onCancel: () => void;
  isSubmitting?: boolean;
}

export default function TaxonomyNodeForm({
  node,
  parentNode,
  allNodes,
  onSubmit,
  onCancel,
  isSubmitting = false,
}: TaxonomyNodeFormProps) {
  const { t } = useTranslation();
  const isEditMode = !!node;

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<TaxonomyNodeFormData>({
    resolver: zodResolver(taxonomyNodeSchema),
    defaultValues: {
      name: node?.name || '',
      description: node?.description || '',
      parentId: node?.parentId || parentNode?.id || null,
      color: node?.color || '',
    },
  });

  // Reset form when node changes
  useEffect(() => {
    reset({
      name: node?.name || '',
      description: node?.description || '',
      parentId: node?.parentId || parentNode?.id || null,
      color: node?.color || '',
    });
  }, [node, parentNode, reset]);

  // Filter out the current node and its descendants from parent options (prevent circular references)
  const getAvailableParents = (): TaxonomyNode[] => {
    if (!isEditMode) {
      return allNodes;
    }

    const nodeId = node!.id;
    const descendants = new Set<string>();

    // Recursively find all descendants
    const findDescendants = (parentId: string) => {
      allNodes.forEach((n) => {
        if (n.parentId === parentId) {
          descendants.add(n.id);
          findDescendants(n.id);
        }
      });
    };

    findDescendants(nodeId);

    // Filter out self and descendants
    return allNodes.filter((n) => n.id !== nodeId && !descendants.has(n.id));
  };

  const availableParents = getAvailableParents();

  // Sort parents by path for hierarchical display
  const sortedParents = [...availableParents].sort((a, b) => {
    return (a.path || a.name).localeCompare(b.path || b.name);
  });

  const handleFormSubmit = async (data: TaxonomyNodeFormData) => {
    // Convert empty strings to undefined (backend expects optional fields, not null)
    const cleanedData: any = {
      name: data.name.trim(),
    };

    // Only include non-empty optional fields
    if (data.description && data.description.trim()) {
      cleanedData.description = data.description.trim();
    }

    if (data.parentId) {
      cleanedData.parentId = data.parentId;
    }

    if (data.color && data.color.trim()) {
      cleanedData.color = data.color.trim();
    }

    await onSubmit(cleanedData);
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4 overflow-y-auto">
      <div className="bg-white dark:bg-gray-900 border-2 border-black dark:border-white rounded-lg p-6 max-w-2xl w-full my-8">
        <h2 className="text-2xl font-bold mb-4 dark:text-white">
          {isEditMode
            ? t('settings.taxonomy.editNode', 'Edit Category')
            : parentNode
            ? t('settings.taxonomy.addChildNode', 'Add Child Category')
            : t('settings.taxonomy.createNode', 'Create Category')}
        </h2>

        {parentNode && !isEditMode && (
          <div className="mb-4 p-3 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded">
            <p className="text-sm text-blue-900 dark:text-blue-200">
              {t('settings.taxonomy.parentInfo', 'Creating child category under:')} <strong>{parentNode.name}</strong>
            </p>
          </div>
        )}

        <form onSubmit={handleSubmit(handleFormSubmit)} className="space-y-4">
          {/* Name */}
          <Input
            {...register('name')}
            label={t('settings.taxonomy.form.name', 'Name')}
            placeholder={t('settings.taxonomy.form.namePlaceholder', 'e.g., Technology, Marketing, Products')}
            error={errors.name?.message}
            required
          />

          {/* Description */}
          <Textarea
            {...register('description')}
            label={t('settings.taxonomy.form.description', 'Description')}
            placeholder={t(
              'settings.taxonomy.form.descriptionPlaceholder',
              'Describe what this category represents...'
            )}
            error={errors.description?.message}
            rows={3}
          />

          {/* Parent Selection */}
          <Select
            {...register('parentId')}
            label={t('settings.taxonomy.form.parent', 'Parent Category')}
            error={errors.parentId?.message}
          >
            <option value="">
              {t('settings.taxonomy.form.noParent', 'None (Root Level)')}
            </option>
            {sortedParents.map((parent) => (
              <option key={parent.id} value={parent.id}>
                {parent.path ? parent.path.replace(/\./g, ' > ') : parent.name} (L{parent.level})
              </option>
            ))}
          </Select>

          {/* Color */}
          <div>
            <Input
              {...register('color')}
              label={t('settings.taxonomy.form.color', 'Color (Hex)')}
              placeholder="#3B82F6"
              error={errors.color?.message}
              maxLength={7}
            />
            <p className="text-xs text-gray-600 dark:text-gray-400 mt-1">
              {t('settings.taxonomy.form.colorHint', 'Format: #RRGGBB (e.g., #3B82F6)')}
            </p>
          </div>

          {/* Actions */}
          <div className="flex gap-2 justify-end pt-4 border-t border-gray-200 dark:border-gray-700">
            <Button type="button" variant="secondary" onClick={onCancel} disabled={isSubmitting}>
              {t('common.cancel', 'Cancel')}
            </Button>
            <Button type="submit" disabled={isSubmitting}>
              {isSubmitting
                ? t('common.saving', 'Saving...')
                : isEditMode
                ? t('common.update', 'Update')
                : t('common.create', 'Create')}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}
